import { Document, Font, Page, PDFViewer, StyleSheet, Text, View } from "@react-pdf/renderer";
import { inject, observer } from "mobx-react";
import React from "react";

Font.register({
  src: "https://cdn.jsdelivr.net/npm/dejavu-sans@1.0.0/fonts/dejavu-sans-webfont.ttf",
  family: "dejavu"
});

const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontFamily: "dejavu"
  },

  space_between: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between"
  },

  label: {
    fontSize: "10pt",
    paddingLeft: 0,
    paddingTop: 2,
    marginBottom: 2
  },

  option: {
    fontSize: "10pt",
    color: "gray"
  },

  bundled_scale_question: {
    marginBottom: 2,
    borderBottomWidth: 1,
    borderColor: "#aaa",
    borderStyle: "dotted",
    fontSize: "10pt"
  },

  bundled_scale_option: {
    paddingTop: 2,
    width: 45,
    textAlign: "center"
  }
});

const PrintContainer = ({ store }) => {
  const { $survey, $question, $locale, $group, $config } = store;

  return (
    $survey.selected &&
    $question.status === "done" && (
      <PDFViewer>
        <Document>
          <Page size="A4" style={styles.page}>
            <Text style={{ fontSize: "18pt" }}>
              {$locale.t($survey.selected._key + "_surveyName") || $survey.selected.name} (
              {$locale.getLanguageLabelForKey($locale.activeLocale, "NL")})
            </Text>
            {$survey.selected.questionTree.map((question, i) => (
              <RenderQuestion key={"parent" + i} store={store} question={question} order={i + 1} />
            ))}
            <Text
              style={{ textAlign: "right", fontSize: "9pt" }}
              render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
              fixed
            />
          </Page>
        </Document>
      </PDFViewer>
    )
  );
};

const RenderQuestion = ({ store, question, order }) => {
  const { $locale, $config, $survey } = store;
  const description = $locale.t(question._key + "_description");

  const wrap =
    $survey.selected._key === "TpbTSeMbJbwjCoEaBRAr" && question.level === 0
      ? true
      : question.level === 1
      ? true
      : false;

  switch (question.type) {
    case "fieldset":
      return (
        <View
          style={{
            marginBottom: 15
          }}
          wrap={wrap}
        >
          <View style={[styles.space_between]}>
            <View style={{ width: "60%" }}>
              <Text
                style={{
                  fontSize: question.level ? "12pt" : "15pt",
                  marginBottom: 2,
                  fontWeight: question.level ? "bold" : "normal",
                  color: question.level ? "grey" : "black"
                }}
              >
                {order}/ {$locale.t(question._key)}
              </Text>
            </View>
            {question.children.every(
              child => child.type === question.children[0].type && child.type.indexOf("scale") > -1
            ) && (
              <View
                style={{
                  width: "40%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end"
                }}
              >
                {scaleOptions(question, $config).map((option, i) => (
                  <Text
                    key={"option" + i}
                    style={[styles.bundled_scale_option, { fontSize: "7pt", color: "grey" }]}
                  >
                    &nbsp;
                    {getTranslation($locale, option, question._key, i)}
                    &nbsp;
                  </Text>
                ))}
              </View>
            )}
            <Text>{description}</Text>
          </View>

          {question.children.map((child, i) => (
            <RenderQuestion key={"child" + i} store={store} question={child} order={i + 1} />
          ))}
        </View>
      );
    case "scale":
      return <Scale store={store} question={question} order={order} />;
    case "list":
      return (
        <View style={{ marginBottom: 10 }}>
          <Text style={[styles.label]}>
            {order}/ {$locale.t(question._key)}
          </Text>
          {question.typeOptions &&
            question.typeOptions.map((option, i) => (
              <Text key={option + i} style={[styles.option]}>
                O{"  "}
                {getTranslation($locale, option, question._key, i)}
              </Text>
            ))}
        </View>
      );
    case "scale_custom":
      return <Scale store={store} question={question} order={order} />;
    case "boolean":
      return (
        <View style={{ marginBottom: 10 }}>
          <Text style={[styles.label]}>
            {order}/ {$locale.t(question._key)}
          </Text>
          <View
            style={{
              display: "flex",
              flexDirection: "row"
            }}
          >
            <Text style={[styles.option]}>O {$locale.t("boolean_yes")}</Text>
            <Text style={[styles.option, { marginLeft: 10 }]}>O {$locale.t("boolean_no")}</Text>
          </View>
        </View>
      );
    case "text":
      return (
        <View style={{ marginBottom: 10 }}>
          <Text
            style={
              ([styles.label],
              {
                marginBottom: 2
              })
            }
          >
            {order}/ {$locale.t(question._key)}
          </Text>
          <View
            style={{
              height: 150,
              backgroundColor: "#eeeeee"
            }}
          />
        </View>
      );
    default:
      return (
        <Text
          style={{
            display: "flex",
            flexDirection: "row"
          }}
        >
          {question.type}
        </Text>
      );
  }
};

const Scale = ({ store, question, order }) => {
  const { $locale, $question, $config } = store;
  const siblings = $question.list.get(question._key).siblings;

  const options = scaleOptions($question.list.get(question._key), $config);

  return siblings.every(child => child.type === siblings[0].type) ? (
    <View style={[styles.bundled_scale_question, styles.space_between]}>
      <View style={{ width: "60%" }}>
        <Text style={[styles.label]}>
          {order}/ {$locale.t(question._key)}
        </Text>
      </View>
      <View
        style={{
          width: "40%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end"
        }}
      >
        {options.map((option, i) => (
          <Text key={"option-a" + i} style={[styles.bundled_scale_option]}>
            O
          </Text>
        ))}
      </View>
    </View>
  ) : (
    <View style={{ marginBottom: 8 }}>
      <View>
        <Text style={[styles.label]}>
          {order}/ {$locale.t(question._key)}
        </Text>
      </View>
      <View style={[styles.space_between]}>
        {options.map((option, i) => (
          <Text key={"option-b" + i} style={[styles.option]}>
            O {"  "}
            {getTranslation($locale, option, question._key, i)}
          </Text>
        ))}
      </View>
    </View>
  );
};

const getTranslation = ($locale, option, key, i) => {
  let label =
    (option.locale && option.locale[$locale.activeLocale]) ||
    $locale.t(key + "_option" + i) ||
    $locale.t("scale_" + option.key) ||
    option.label;
  return label;
};

const scaleOptions = (question, $config) => {
  if (question.type === "fieldset" && question.children[0]) {
    const type = $config.field_types.find(type => type.value === question.children[0].type);
    if (type) {
      return type.options || question.typeOptions || [];
    } else {
      return question.children[0].typeOptions.toJS() || [];
    }
  } else {
    const type = $config.field_types.find(type => type.value === question.type);
    const typeOptions = (type && type.options) || question.translatedTypeOptions || [];

    return typeOptions;
  }
};

export default inject("store")(observer(PrintContainer));
